'use client'

import React, { useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@/components/ui/button'
import {
  FileIcon,
  ReloadIcon,
  Cross2Icon,
  CheckCircledIcon,
} from '@radix-ui/react-icons'
import { UPLOAD_CUSTOMER_FILE } from '@/containers/B2BCustomers/B2BCustomersQueries'
import {
  ALLOWED_EXTENSIONS,
  MAX_CUSTOMER_FILE_BYTES,
  MAX_CUSTOMER_FILE_MB,
} from './CustomerFileRowEditor'

export interface BulkCustomerFileUploadResult {
  storage_key: string
  original_name?: string | null
  mime_type?: string | null
  size_bytes?: number | null
}

export interface BulkCustomerFileEntry {
  id: string
  file: File
  status: 'uploading' | 'done' | 'error'
  error?: string
  result?: BulkCustomerFileUploadResult
}

const formatBytes = (bytes?: number | null): string => {
  if (!bytes) return ''
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface BulkCustomerFilesUploaderProps {
  entries: BulkCustomerFileEntry[]
  onEntriesChange: (
    updater: (prev: BulkCustomerFileEntry[]) => BulkCustomerFileEntry[]
  ) => void
  disabled?: boolean
}

// Multi-file picker for the "bulk add files to customers" flow. Each picked
// file is uploaded immediately (mirrors CustomerFileRowEditor's single-file
// flow) so the admin sees per-file progress/errors before the final
// bulkAddCustomerFiles mutation is ever fired.
export function BulkCustomerFilesUploader({
  entries,
  onEntriesChange,
  disabled = false,
}: BulkCustomerFilesUploaderProps) {
  const [uploadCustomerFile] = useMutation(UPLOAD_CUSTOMER_FILE)

  const uploadOne = useCallback(
    async (file: File) => {
      const id = `${file.name}-${file.size}-${file.lastModified}-${Math.random()
        .toString(36)
        .slice(2)}`
      const ext = (file.name.split('.').pop() ?? '').toLowerCase()

      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        onEntriesChange(prev => [
          ...prev,
          {
            id,
            file,
            status: 'error',
            error: `Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed.`,
          },
        ])
        return
      }
      if (file.size > MAX_CUSTOMER_FILE_BYTES) {
        onEntriesChange(prev => [
          ...prev,
          {
            id,
            file,
            status: 'error',
            error: `This file is ${(file.size / (1024 * 1024)).toFixed(
              1
            )} MB — the limit is ${MAX_CUSTOMER_FILE_MB} MB.`,
          },
        ])
        return
      }

      onEntriesChange(prev => [...prev, { id, file, status: 'uploading' }])

      try {
        const { data } = await uploadCustomerFile({ variables: { file } })
        const result = data?.uploadCustomerFile
        onEntriesChange(prev =>
          prev.map(entry =>
            entry.id === id
              ? result
                ? { ...entry, status: 'done', result }
                : { ...entry, status: 'error', error: 'Upload failed' }
              : entry
          )
        )
      } catch (e: any) {
        console.error('Customer file upload error:', e)
        onEntriesChange(prev =>
          prev.map(entry =>
            entry.id === id
              ? { ...entry, status: 'error', error: e?.message ?? 'Upload failed' }
              : entry
          )
        )
      }
    },
    [onEntriesChange, uploadCustomerFile]
  )

  const handleFilesInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? [])
      files.forEach(file => {
        uploadOne(file)
      })
      // Reset so the same file(s) can be re-selected after a validation error.
      event.target.value = ''
    },
    [uploadOne]
  )

  const handleRemove = (id: string) => {
    onEntriesChange(prev => prev.filter(entry => entry.id !== id))
  }

  const inputId = 'bulk-customer-files-input'

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4 rounded-lg border-2 border-dashed border-gray-200 p-3">
        <p className="text-sm text-gray-500">
          Upload one or more files ({ALLOWED_EXTENSIONS.join(', ')} — max{' '}
          {MAX_CUSTOMER_FILE_MB} MB each)
        </p>
        <input
          type="file"
          id={inputId}
          multiple
          accept={ALLOWED_EXTENSIONS.map(e => `.${e}`).join(',')}
          className="hidden"
          onChange={handleFilesInput}
          disabled={disabled}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <FileIcon className="mr-2 h-4 w-4" />
          Choose files
        </Button>
      </div>

      {entries.length > 0 && (
        <ul className="space-y-2 max-h-60 overflow-y-auto">
          {entries.map(entry => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-2 rounded-md border p-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <FileIcon className="h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <p className="truncate text-sm">{entry.file.name}</p>
                  {entry.status === 'uploading' && (
                    <p className="text-xs text-muted-foreground">Uploading…</p>
                  )}
                  {entry.status === 'done' && (
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(entry.result?.size_bytes)}
                    </p>
                  )}
                  {entry.status === 'error' && (
                    <p className="text-xs text-destructive">{entry.error}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {entry.status === 'uploading' && (
                  <ReloadIcon className="h-4 w-4 animate-spin" />
                )}
                {entry.status === 'done' && (
                  <CheckCircledIcon className="h-4 w-4 text-green-600" />
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() => handleRemove(entry.id)}
                  aria-label={`Remove ${entry.file.name}`}
                >
                  <Cross2Icon className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
